import PropTypes from 'prop-types';
import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { formatNumber, formatPercent, ratio } from './format';
import { reportShort } from './reports';

/**
 * Santralden agent'a giden yol. Üç raporun üç farklı "gelen çağrı" sayısı
 * vermesi bu ekranın en kritik noktası; huni farkı gizlemek yerine görünür
 * kılıyor.
 */
const CallFunnel = ({ summary, loading }) => {
  if (loading || !summary) {
    return (
      <Box sx={{ display: 'grid', gap: 1.25 }}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} height={32} variant="rounded" />
        ))}
      </Box>
    );
  }

  const total = Number(summary.switchboardCalls);

  // Payda yoksa huni çizilemez; bozuk genişlikli çubuk yerine durumu yaz.
  if (!Number.isFinite(total) || total <= 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        Bu gün için çağrı verisi bulunamadı.
      </Typography>
    );
  }

  const steps = [
    { label: 'Santrale gelen', source: reportShort(14), value: summary.switchboardCalls, tone: 'full' },
    { label: 'Kuyruğa düşen', source: reportShort(1), value: summary.queuedCalls, tone: 'mid' },
    { label: 'Agent cevapladı', source: reportShort(1), value: summary.answered, tone: 'mid' },
    { label: 'Kuyrukta kaçan', source: reportShort(1), value: summary.abandoned, tone: 'loss' }
  ];

  return (
    <Box sx={{ display: 'grid', gap: 1.25 }}>
      {steps.map((step) => (
        <Box
          key={step.label}
          sx={{
            display: 'grid',
            gridTemplateColumns: '112px 1fr',
            gap: 1.5,
            alignItems: 'center'
          }}
        >
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, lineHeight: 1.3 }}>
              {step.label}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
              {step.source}
            </Typography>
          </Box>

          <Box
            sx={(theme) => ({
              height: 32,
              borderRadius: 1,
              backgroundColor:
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,.06)' : theme.palette.grey[100],
              overflow: 'hidden'
            })}
          >
            <Box
              sx={(theme) => {
                const bg = {
                  full: theme.palette.primary.main,
                  mid: theme.palette.mode === 'dark' ? 'rgba(255,167,38,.45)' : '#FFCC80',
                  loss: theme.palette.mode === 'dark' ? '#8492C4' : '#90A4AE'
                }[step.tone];
                return {
                  height: '100%',
                  width: `${Math.max(ratio(step.value, total) || 0, 8)}%`,
                  minWidth: 104,
                  borderRadius: 1,
                  backgroundColor: bg,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  color: step.tone === 'loss' ? theme.palette.common.white : '#3E2600'
                };
              }}
            >
              <Typography
                sx={{ fontSize: '0.84rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
              >
                {formatNumber(step.value)}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', opacity: 0.85 }}>
                {formatPercent(ratio(step.value, total))}
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

CallFunnel.propTypes = {
  summary: PropTypes.object,
  loading: PropTypes.bool
};

export default CallFunnel;
